import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import DateFilter from '../components/DateFilter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function JobWorkDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFilters, setDateFilters] = useState({ startDate: null, endDate: null });

  useEffect(() => {
    fetchDashboard();
  }, [dateFilters]);

  const fetchDashboard = async () => {
    try {
      const params = {};
      if (dateFilters.startDate) params.start_date = dateFilters.startDate;
      if (dateFilters.endDate) params.end_date = dateFilters.endDate;
      
      const response = await axios.get(`${API}/jobwork/dashboard`, { params });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB');
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  if (!stats) {
    return <div className="text-center py-8 text-red-500">Failed to load dashboard</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Job Work Dashboard</h1>
        <div className="flex gap-2">
          <Link to="/jobwork-pos" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
            + New PO
          </Link>
          <Link to="/jobwork-challans" className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">
            + New Challan
          </Link>
        </div>
      </div>

      {/* Date Filter */}
      <DateFilter onFilterChange={setDateFilters} />

      {/* Summary Cards - Row 1 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-5">
          <p className="text-sm text-gray-600">Total Vendors</p>
          <p className="text-3xl font-bold text-gray-900">{stats.total_vendors}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-5">
          <p className="text-sm text-gray-600">Total POs</p>
          <p className="text-3xl font-bold text-blue-600">{stats.total_pos}</p>
          <div className="text-xs text-gray-500 mt-1">
            {stats.approved_pos} approved • {stats.pending_pos} pending
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-5">
          <p className="text-sm text-gray-600">Total Challans</p>
          <p className="text-3xl font-bold text-green-600">{stats.total_challans}</p>
          <div className="text-xs text-gray-500 mt-1">
            {stats.sent_challans} sent • {stats.completed_challans} completed
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-5 border-l-4 border-red-500">
          <p className="text-sm text-gray-600">Overdue POs</p>
          <p className="text-3xl font-bold text-red-600">{stats.overdue_pos}</p>
          <Link to="/jobwork-pos" className="text-xs text-red-600 hover:underline">
            View overdue →
          </Link>
        </div>
      </div>

      {/* QC Summary Cards - Row 2 */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg shadow p-5">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quality Control Summary</h2>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <div className="bg-white rounded-lg p-4 text-center">
            <p className="text-xs text-gray-500">Pending QC</p>
            <p className="text-2xl font-bold text-purple-600">{stats.pending_qc_items}</p>
          </div>
          <div className="bg-white rounded-lg p-4 text-center">
            <p className="text-xs text-gray-500">Total Inspected</p>
            <p className="text-2xl font-bold text-blue-600">{stats.total_inspected.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-lg p-4 text-center">
            <p className="text-xs text-gray-500">Approved</p>
            <p className="text-2xl font-bold text-green-600">{stats.total_approved.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-lg p-4 text-center">
            <p className="text-xs text-gray-500">Rejected</p>
            <p className="text-2xl font-bold text-red-600">{stats.total_rejected.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-lg p-4 text-center">
            <p className="text-xs text-gray-500">Rework</p>
            <p className="text-2xl font-bold text-orange-600">{stats.total_rework.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-lg p-4 text-center">
            <p className="text-xs text-gray-500">Accept Rate</p>
            <p className={`text-2xl font-bold ${stats.acceptance_rate >= 95 ? 'text-green-600' : stats.acceptance_rate >= 80 ? 'text-yellow-600' : 'text-red-600'}`}>
              {stats.acceptance_rate}%
            </p>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Link to="/quality-inspection" className="text-sm text-purple-600 hover:text-purple-800 font-medium">
            Go to QC Inspection →
          </Link>
        </div>
      </div>

      {/* Challan Status Breakdown */}
      <div className="bg-white rounded-lg shadow p-5">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Challan Pipeline</h2>
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-gray-100 rounded-full h-8 overflow-hidden flex">
            {stats.total_challans > 0 && (
              <>
                <div 
                  className="bg-blue-500 h-full flex items-center justify-center text-white text-xs font-medium"
                  style={{ width: `${(stats.sent_challans / stats.total_challans) * 100}%` }}
                >
                  {stats.sent_challans > 0 && `Sent (${stats.sent_challans})`}
                </div>
                <div 
                  className="bg-yellow-500 h-full flex items-center justify-center text-white text-xs font-medium"
                  style={{ width: `${(stats.partially_received / stats.total_challans) * 100}%` }}
                >
                  {stats.partially_received > 0 && `Partial (${stats.partially_received})`}
                </div>
                <div 
                  className="bg-purple-500 h-full flex items-center justify-center text-white text-xs font-medium"
                  style={{ width: `${(stats.pending_qc_challans / stats.total_challans) * 100}%` }}
                >
                  {stats.pending_qc_challans > 0 && `QC (${stats.pending_qc_challans})`}
                </div>
                <div 
                  className="bg-green-500 h-full flex items-center justify-center text-white text-xs font-medium"
                  style={{ width: `${(stats.completed_challans / stats.total_challans) * 100}%` }}
                >
                  {stats.completed_challans > 0 && `Done (${stats.completed_challans})`}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-500">
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-blue-500 rounded"></span> Sent</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-yellow-500 rounded"></span> Partially Received</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-purple-500 rounded"></span> Pending QC</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-500 rounded"></span> Completed</span>
        </div>
      </div>

      {/* Recent Activity Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent POs */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">Recent POs</h2>
            <Link to="/jobwork-pos" className="text-sm text-blue-600 hover:underline">View All</Link>
          </div>
          <div className="p-4">
            {stats.recent_pos && stats.recent_pos.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_pos.map((po, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                    <div>
                      <div className="font-medium text-blue-600 text-sm">{po.po_number}</div>
                      <div className="text-xs text-gray-500">{po.vendor_name}</div>
                    </div>
                    <div className="text-right">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        po.status === 'Completed' ? 'bg-green-100 text-green-800' :
                        po.approval_status === 'Approved' ? 'bg-blue-100 text-blue-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {po.status}
                      </span>
                      <div className="text-xs text-gray-400 mt-1">{formatDate(po.po_date)}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4 text-sm">No recent POs</p>
            )}
          </div>
        </div>

        {/* Recent Challans */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">Recent Challans</h2>
            <Link to="/jobwork-challans" className="text-sm text-blue-600 hover:underline">View All</Link>
          </div>
          <div className="p-4">
            {stats.recent_challans && stats.recent_challans.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_challans.map((ch, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                    <div>
                      <div className="font-medium text-green-600 text-sm">{ch.challan_number}</div>
                      <div className="text-xs text-gray-500">{ch.vendor_name}</div>
                    </div>
                    <div className="text-right">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        ch.status === 'Completed' ? 'bg-green-100 text-green-800' :
                        ch.status === 'Received - Pending QC' ? 'bg-purple-100 text-purple-800' :
                        ch.status === 'Partially Received' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {ch.status}
                      </span>
                      <div className="text-xs text-gray-400 mt-1">{formatDate(ch.challan_date)}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4 text-sm">No recent challans</p>
            )}
          </div>
        </div>

        {/* Recent QC Inspections */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">Recent QC</h2>
            <Link to="/quality-inspection" className="text-sm text-blue-600 hover:underline">View All</Link>
          </div>
          <div className="p-4">
            {stats.recent_qc && stats.recent_qc.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_qc.map((qc, index) => {
                  const totalInspected = qc.items?.reduce((sum, item) => sum + (item.inspected_quantity || 0), 0) || 0;
                  const totalApproved = qc.items?.reduce((sum, item) => sum + (item.approved_quantity || 0), 0) || 0;
                  const totalRejected = qc.items?.reduce((sum, item) => sum + (item.rejected_quantity || 0), 0) || 0;
                  return (
                    <div key={index} className="py-2 border-b border-gray-100 last:border-0">
                      <div className="flex justify-between items-center">
                        <div className="font-medium text-purple-600 text-sm">{qc.challan_id?.substring(0, 8)}...</div>
                        <div className="text-xs text-gray-400">{formatDate(qc.inspection_date)}</div>
                      </div>
                      <div className="flex gap-2 mt-1 text-xs">
                        <span className="text-blue-600">{totalInspected} checked</span>
                        <span className="text-green-600">{totalApproved} ✓</span>
                        {totalRejected > 0 && <span className="text-red-600">{totalRejected} ✗</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4 text-sm">No recent inspections</p>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-5">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Link to="/jobwork-pos" className="p-4 bg-blue-50 rounded-lg hover:bg-blue-100 text-center">
            <div className="text-2xl mb-1">📋</div>
            <div className="text-sm font-medium text-blue-700">Purchase Orders</div>
          </Link>
          <Link to="/jobwork-challans" className="p-4 bg-green-50 rounded-lg hover:bg-green-100 text-center">
            <div className="text-2xl mb-1">📦</div>
            <div className="text-sm font-medium text-green-700">Challans</div>
          </Link>
          <Link to="/quality-inspection" className="p-4 bg-purple-50 rounded-lg hover:bg-purple-100 text-center">
            <div className="text-2xl mb-1">🔍</div>
            <div className="text-sm font-medium text-purple-700">QC Inspection</div>
          </Link>
          <Link to="/vendor-performance" className="p-4 bg-orange-50 rounded-lg hover:bg-orange-100 text-center">
            <div className="text-2xl mb-1">📊</div>
            <div className="text-sm font-medium text-orange-700">Vendor Performance</div>
          </Link>
          <Link to="/jobwork-vendors" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 text-center">
            <div className="text-2xl mb-1">🏭</div>
            <div className="text-sm font-medium text-gray-700">Vendors & Items</div>
          </Link>
        </div>
      </div>
    </div>
  );
}
